import { Button, Flex, Icon, Stack } from '@chakra-ui/react';
import { getTenantId } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { dimensionUnitOptions, PermissionLevel, weightUnitOptions } from '@pelicargo/utils';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { HiArrowRight, HiOutlineClipboardDocumentList } from 'react-icons/hi2';

import { useAuth } from '../../context/AuthProvider';
import { useContacts } from '../../hooks/contact/useContacts';
import { useModEnter } from '../../hooks/useModEnter';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleTextarea } from '../SimpleTextarea';

type Props = {
  name: string;
  hasLongerVersion?: boolean;
  onCancel?: () => void;
  onSubmit?: () => Promise<void>;
  children?: ReactNode;
  // For some reason, "request clarification" is unlike all the other email forms.
  // This just is for compatibility
  showPelicargoOnlyWarning?: boolean;
  hideFields?: string[]; // e.g. ['origin', 'destination', 'weightUnit', 'dimensionUnit'] if you want to hide those fields because they don't make sense for a specific email type
};

export const SimpleSharedEmailForm = ({
  name,
  hasLongerVersion = false,
  onCancel,
  onSubmit,
  children,
  showPelicargoOnlyWarning = false,
  hideFields = [],
}: Props) => {
  const { user } = useAuth();
  const [senders] = useContacts({ minPermission: PermissionLevel.CSMEMBER, tenant: getTenantId() });
  const [isCopying, setIsCopying] = useState(false);
  const [isCopyingBody, setIsCopyingBody] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setValue } = useFormContext();

  const providers = useWatch({ name: `${name}.providers` });

  // Get a list of providers that 1) have selected contacts 2) where those contacts are all Pelicargo
  const warnings = providers
    ?.map((provider) => {
      const peliEmails = provider?.recipients
        ?.map((contact) => contact?.email?.includes('@pelicargo.com'))
        .filter(Boolean);
      if (!!peliEmails?.length && peliEmails?.length === provider?.recipients?.length) {
        return provider?.id;
      }
    })
    .filter(Boolean);

  const defaultContact = useMemo(() => {
    return senders?.find((contact) => contact.email === user?.email);
  }, [senders, user?.email]);

  useEffect(() => {
    if (defaultContact?.id) {
      setValue(`${name}.senderContactId`, defaultContact.id);
    }
  }, [defaultContact?.id]);

  const setClipboard = async (contentOnly) => {
    setValue(`${name}.contentOnly`, contentOnly);
    setValue(`${name}.clipboard`, true);
    await onSubmit?.();
    setValue(`${name}.contentOnly`, false);
    setValue(`${name}.clipboard`, false);
  };

  const handleCopyBody = async (contentOnly) => {
    setIsCopyingBody(true);
    await setClipboard(contentOnly);
    setIsCopyingBody(false);
  };

  const handleCopy = async (contentOnly) => {
    setIsCopying(true);
    await setClipboard(contentOnly);
    setIsCopying(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    await onSubmit?.();
    setIsSubmitting(false);
  };

  const submitRef = useModEnter();

  return (
    <Stack spacing={6}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Stack direction="row" alignItems="center" flex={2} divider={<Icon as={HiArrowRight} />}>
          {/* Intentionally SimpleInput, not AirportInput as the team often adds multiple options here and it's faster to type than select */}
          {!hideFields.includes('origin') && (
            <SimpleInput flex={1} size="md" name={`${name}.origin`} placeholder="LAX" label="Origin" ignoreOnBlur />
          )}
          {/* Intentionally SimpleInput, not AirportInput as the team often adds multiple options here and it's faster to type than select */}
          {!hideFields.includes('destination') && (
            <SimpleInput
              flex={1}
              size="md"
              name={`${name}.destination`}
              placeholder="LHR"
              label="Destination"
              ignoreOnBlur
            />
          )}
        </Stack>
        <Show if={!hideFields.includes('weightUnit')}>
          <SimpleSelect
            flex={1}
            name={`${name}.weightUnit`}
            label="Weight Unit"
            ignoreOnBlur
            options={weightUnitOptions}
          />
        </Show>
        <Show if={!hideFields.includes('dimensionUnit')}>
          <SimpleSelect
            flex={1}
            name={`${name}.dimensionUnit`}
            label="Dimension Unit"
            ignoreOnBlur
            options={dimensionUnitOptions}
          />
        </Show>
      </Stack>
      <Show if={!hideFields.includes('senderContactId')}>
        <SimpleSelect
          name={`${name}.senderContactId`}
          label="Sender"
          ignoreOnBlur
          options={senders?.map((contact) => ({
            label: `${contact?.full_name} <${contact?.email}>`,
            value: contact?.id,
          }))}
        />
      </Show>
      <Show if={!hideFields.includes('customMessage')}>
        <SimpleTextarea
          label="Custom Message (optional)"
          placeholder="This will get inserted into the email you send"
          name={`${name}.customMessage`}
          ignoreOnBlur
        />
      </Show>

      <Show if={!!children}>{children}</Show>

      <Show if={hasLongerVersion}>
        <SimpleCheckbox name={`${name}.isLongEmail`} label="Send a longer email" />
      </Show>

      <Stack direction="column">
        <Show if={!!warnings?.length}>
          <Flex color="orange.500">Only Pelicargo emails on: {warnings?.join(', ')}</Flex>
        </Show>
        <Show if={showPelicargoOnlyWarning}>
          <Flex color="orange.500">Only Pelicargo emails are specified</Flex>
        </Show>
        <Stack spacing={4} direction="row" justify="flex-end">
          <Show if={!!onCancel}>
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          </Show>
          <Button
            colorScheme="blue"
            isLoading={isCopyingBody}
            rightIcon={<HiOutlineClipboardDocumentList />}
            onClick={() => handleCopyBody(true)}
          >
            Copy (Body Only)
          </Button>
          <Button
            colorScheme="blue"
            isLoading={isCopying}
            rightIcon={<HiOutlineClipboardDocumentList />}
            onClick={() => handleCopy(false)}
          >
            Copy
          </Button>
          <Button
            colorScheme="brand"
            ref={submitRef}
            isLoading={isSubmitting && !isCopying && !isCopyingBody}
            rightIcon={<HiArrowRight />}
            onClick={handleSubmit}
          >
            Send Email(s)
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
